import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useTheme } from '@mui/material';
import {
	confirmPasswordReset,
	getAuth,
	verifyPasswordResetCode,
} from 'firebase/auth';

import { useAuthLogin, useResetPassword } from '@mopla/business-logic';
import { LoginFields, SuccessStep } from '@mopla/constants';
import {
	ArrowRightIcon,
	BottomSlider,
	DRIVER_ANDROID_LINK,
	DRIVER_IOS_LINK,
	DriverChangePasswordForm,
	DriverForgotPasswordForm,
	DriverLoginForm,
	DropdownIcon,
	MplButton,
	NativePageBackground,
	RightRideAnim,
	StoreBadges,
} from '@mopla/ui';

import { environment } from '../../../environments/environment';

import { WelcomeStep } from './welcome';
import {
	BrowserTitle,
	DrawerContent,
	LoginBox,
	Root,
	SubmitLevel,
	WelcomeRoot,
	WelcomeTitle,
	Wrapper,
} from './WelcomeScreen.styles';

export interface IFormInput {
	[LoginFields.email]: string;
	[LoginFields.password]?: string;
}

export const WelcomeScreen = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const resetPassword = useResetPassword('DRIVER', environment.baseUrl);
	const { t } = useTranslation(['welcome', 'logIn', 'changePassword']);
	const [forgotPasswordError, setForgotPasswordError] = useState(false);
	const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');

	const [successType, setSuccessType] = useState('');
	const [showLogin, setShowLogin] = useState(false);

	const { onLogin, errorMessage, authError, validateUser } = useAuthLogin();

	const [forgotPasswordIsActive, setForgotPasswordIsActive] = useState(false);
	const [changePasswordIsActive, setChangePasswordIsActive] = useState(false);

	useEffect(() => {
		const showForgotPassword = location.search.includes(
			WelcomeStep.forgotPassword
		);
		const showChangePasswordIsActive = location.search.includes(
			WelcomeStep.resetPassword
		);
		setForgotPasswordIsActive(showForgotPassword);
		setChangePasswordIsActive(showChangePasswordIsActive);
	}, [location.search]);

	const onSubmitForgotPassword = (data: IFormInput) => {
		const { email } = data;
		setForgotPasswordError(false);

		return resetPassword(email)
			.then((res) => {
				if (res.status === 204) {
					return setSuccessType(SuccessStep.forgotPasswordEmailSent);
				}
				return setForgotPasswordError(true);
			})
			.catch((err) => {
				setForgotPasswordError(true);
				console.log('Error on reset password', err);
			});
	};

	const drawerOpen = forgotPasswordIsActive || changePasswordIsActive;

	const getChangePasswordTitle = () => {
		if (successType === SuccessStep.passwordChanged)
			return 'text.header.driver_success';
		return 'text.header.driver_header';
	};

	const driverLoginFormEl = (
		<DriverLoginForm
			driverApp
			browser={!Capacitor.isNativePlatform()}
			onLogin={onLogin}
			authError={authError}
			errorMessage={errorMessage}
			validateUser={validateUser}
			socialLoginMethods={[...environment.allowedSocialLoginMethods]}
		/>
	);

	const stepData = () => {
		if (forgotPasswordIsActive)
			return {
				namespace: 'forgotPassword',
				title:
					successType === SuccessStep.forgotPasswordEmailSent
						? 'text.header.success'
						: 'text.header.text',
				mainContent: (
					<DriverForgotPasswordForm
						onSubmit={onSubmitForgotPassword}
						onSuccessCallback={() => setSuccessType('')}
						successStep={successType}
						error={forgotPasswordError}
						closeError={() => setForgotPasswordError(false)}
						setForgotPasswordEmail={setForgotPasswordEmail}
						forgotPasswordEmail={forgotPasswordEmail}
					/>
				),
				onBack: () => navigate(-1),
			};

		return {
			namespace: 'changePassword',
			title: getChangePasswordTitle(),
			mainContent: (
				<DriverChangePasswordForm
					onSuccessCallback={() => setSuccessType(SuccessStep.passwordChanged)}
					verifyPasswordResetCode={verifyPasswordResetCode}
					confirmPasswordReset={confirmPasswordReset}
					getAuth={getAuth}
					showSuccess={successType === SuccessStep.passwordChanged}
					isOnboarding={
						new URLSearchParams(location.search).get('isOnboarding') === 'true'
					}
					onSubmitCallback={() => navigate('/login')}
					onErrorCallback={() => navigate('/login')}
					driverLoginForm={driverLoginFormEl}
				/>
			),
			onBack: () => navigate(-1),
		};
	};

	if (!forgotPasswordIsActive && !changePasswordIsActive) {
		return (
			<Wrapper>
				<WelcomeRoot>
					<WelcomeTitle>{t('text.title_driver')}</WelcomeTitle>
					<RightRideAnim loop />
					{!Capacitor.isNativePlatform() && (
						<>
							<BrowserTitle>
								{t('changePassword:text.download_app')}
							</BrowserTitle>
							<StoreBadges
								iosLink={DRIVER_IOS_LINK}
								androidLink={DRIVER_ANDROID_LINK}
							/>
							<LoginBox
								data-testid="welcomePage-loginBox"
								onClick={() => setShowLogin((prev: boolean) => !prev)}
							>
								<span>{t('changePassword:text.login_in_browser')}</span>
								<DropdownIcon isOpened={showLogin} />
							</LoginBox>
							{showLogin && driverLoginFormEl}
						</>
					)}
				</WelcomeRoot>
				{Capacitor.isNativePlatform() && (
					<SubmitLevel>
						<MplButton
							color="secondary"
							fullWidth
							endIcon={<ArrowRightIcon />}
							onClick={() => navigate('/login')}
						>
							{t('logIn:button.logIn')}
						</MplButton>
					</SubmitLevel>
				)}
				<NativePageBackground bgColor={theme.palette.primary.light} />
			</Wrapper>
		);
	}

	return (
		<Root>
			<BottomSlider
				open={drawerOpen}
				title={t(`${stepData().namespace}:${stepData().title}`)}
				onBack={() => navigate(-1)}
				backgroundColor={theme.palette.primary.light}
			>
				<DrawerContent>{stepData().mainContent}</DrawerContent>
			</BottomSlider>
			<NativePageBackground bgColor={theme.palette.primary.light} />
		</Root>
	);
};
